/**
 * @fileoverview Email verification page.
 * Reads a ?token= query parameter, sends it to the backend for verification,
 * and displays the result (success, invalid, or expired).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Loading from '../components/Loading';

/**
 * VerifyEmail page component.
 * On mount, reads the token from URL search params and calls the backend
 * to verify the email. Shows loading, success, or error states.
 *
 * @returns {JSX.Element} The email verification page
 */
function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'invalid' | 'expired'
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const successRef = useRef(null);

  useEffect(() => {
    if (status === 'success') successRef.current?.focus();
  }, [status]);

  useEffect(() => {
    /**
     * Verify the email token by calling the backend.
     * Sets status based on the response or error code.
     */
    async function verify() {
      if (!token) {
        setStatus('invalid');
        return;
      }
      try {
        await api.verifyEmail({ token });
        setStatus('success');
      } catch (err) {
        if (err.response?.status === 404) {
          setStatus('invalid');
        } else if (err.response?.status === 410) {
          setStatus('expired');
        } else {
          setStatus('invalid');
        }
      }
    }
    verify();
  }, [token]);

  /**
   * Handle resend verification for expired tokens.
   * Requires the user to enter their email address.
   */
  const handleResend = async () => {
    if (!resendEmail.trim()) return;
    setResending(true);
    setResendMessage('');
    try {
      await api.resendVerification({ email: resendEmail.trim().toLowerCase() });
      setResendMessage('Verification email sent! Check your inbox.');
    } catch {
      setResendMessage('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-card">
        <a href="https://cogslog.com" aria-label="COGSLOG home">
          <img src="/logos/stacked/cogslog-stacked-tagline-color.svg" alt="COGSLOG" className="auth-logo" />
        </a>

        {status === 'loading' && (
          <Loading variant="spinner" size="sm" message="Verifying your email..." />
        )}

        {status === 'success' && (
          <div className="verify-email-success">
            <svg
              className="verify-email-icon verify-email-icon--success"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <h2 ref={successRef} tabIndex={-1}>Email Verified!</h2>
            <p>Your email has been verified successfully. You can now sign in.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>
          </div>
        )}

        {status === 'invalid' && (
          <div className="verify-email-error">
            <svg
              className="verify-email-icon verify-email-icon--error"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <h2>Invalid Verification Link</h2>
            <p>This verification link is invalid or has already been used.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>
          </div>
        )}

        {status === 'expired' && (
          <div className="verify-email-expired">
            <svg
              className="verify-email-icon verify-email-icon--warning"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h2>Link Expired</h2>
            <p>This verification link has expired. Enter your email below to receive a new one.</p>
            <div className="verify-email-resend">
              <div className="field">
                <input
                  type="email"
                  className="input"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="Enter your email"
                />
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleResend}
                disabled={resending || !resendEmail.trim()}
              >
                {resending ? 'Sending...' : 'Resend Verification'}
              </button>
              {resendMessage && (
                <p className="verify-email-resend-message">{resendMessage}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
